import { ChangeEvent, Fragment } from "react";
import { useSelector } from "react-redux";
import { faBan, faCloudUploadAlt } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRouter } from "next/navigation";

import { FolderSelector, PublicGallerySwitch, TextInput } from "@/components";
import Button from "@/components/button";
import { ButtonBar, Module } from "@/components/layouts";
import { ButtonGroup } from "@/components/layouts/button-group";
import { AnimationType, PixelMode } from "@/gql/generated/graphql";
import { useSave } from "@/hooks/editor/use-save";
import { useModal } from "@/hooks/use-modal";
import * as animationActions from "@/lib/state/actions/animation-actions";
import { AnimationPixelMode } from "@/lib/state/reducers/animation-default-state";
import { animationSelector } from "@/lib/state/selectors/animation-selector";
import { useAppDispatch } from "@/lib/state/store";

import ModalTitle from "./modal-title";

export default function SaveAnimationModal() {
  const animation = useSelector(animationSelector);
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { closeModal } = useModal();
  const { saveAnimation } = useSave();

  const onPublicSwitchChange = (isPublic: boolean) => {
    dispatch(animationActions.setAnimationPublic(isPublic));
  };

  const onTitleChange = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(animationActions.setAnimationTitle(event.target.value));
  };

  const onFolderChange = (folderId: string | null) => {
    dispatch(animationActions.setAnimationFolder(folderId));
  };

  const onSavePressed = () => {
    saveAnimation();
  };

  return (
    <div>
      <ModalTitle>Save Animation</ModalTitle>
      <Module>
        <p>Changes to the options take effect immediately in the editor.</p>
        <table>
          <tr>
            <td>Title</td>
            <td>
              <TextInput
                defaultValue={animation.title}
                onChange={onTitleChange}
              />
            </td>
          </tr>
          {animation.type === AnimationType.Standalone && (
            <Fragment>
              <tr>
                <td>Folder</td>
                <td>
                  <FolderSelector
                    currentFolderId={animation.folderId}
                    onFolderSelected={onFolderChange}
                  />
                </td>
              </tr>
              <tr>
                <td>Publish</td>
                <td>
                  <PublicGallerySwitch
                    isPublic={animation.public}
                    onAnimationPublicToggled={onPublicSwitchChange}
                  />
                </td>
              </tr>
            </Fragment>
          )}
        </table>
      </Module>
      <ButtonBar>
        <ButtonGroup align="right">
          <Button onClick={closeModal} type="negative">
            <FontAwesomeIcon icon={faBan} /> Cancel
          </Button>
          <Button
            onClick={onSavePressed}
            type="positive"
            icon={faCloudUploadAlt}
            rightAlign
          >
            Save
          </Button>
        </ButtonGroup>
      </ButtonBar>
    </div>
  );
}
